import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle2, Loader2, XCircle, Sparkles } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid" || data.status === "completed") {
          setState("paid");
          return;
        }
      } catch (e) {
        // transient poll failure — retry below
      }
      if (attempts >= 8) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md rounded-xl border p-8 text-center k-fade-in" data-testid="payment-success-card">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-secondary">
          {state === "checking" && <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />}
          {state === "paid" && <CheckCircle2 className="h-7 w-7 text-emerald-600" />}
          {state === "pending" && <Sparkles className="h-7 w-7 text-blue-600" />}
          {state === "error" && <XCircle className="h-7 w-7 text-destructive" />}
        </div>

        {state === "checking" && (
          <>
            <h1 className="font-display text-2xl font-extrabold">Confirming your subscription…</h1>
            <p className="mt-2 text-sm text-muted-foreground">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <h1 className="font-display text-2xl font-extrabold">You're all set!</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Your KlenaPro subscription is active. Billing runs monthly based on your cleaners and rooms.
            </p>
          </>
        )}
        {state === "pending" && (
          <>
            <h1 className="font-display text-2xl font-extrabold">Almost there</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Your payment is processing. It'll appear on your billing page shortly.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <h1 className="font-display text-2xl font-extrabold">Something went wrong</h1>
            <p className="mt-2 text-sm text-muted-foreground">We couldn't confirm this payment.</p>
          </>
        )}

        <Button onClick={() => navigate("/manager/billing")} data-testid="back-to-billing-btn" className="mt-6 w-full">
          Go to Billing
        </Button>
      </Card>
    </div>
  );
}
